import React, {useMemo} from 'react';

import {isSystemMetric} from '../../../../util/plotHelpers/prettifyMetricName';
import {
  type MetricSearchOption,
  MetricSearchSelect,
} from '../../../MetricSearch/MetricSearchSelect';
import {xAxisLabel} from '../utils';
import {DEFAULT_X_AXIS_OPTION_VALUES} from './SemanticLegendDefaults';

/**
 * Only numeric metrics can drive key-based colors: a metric key is bucketed by
 * its min, max, or last value. String-valued keys reach the legend as config
 * categories, which come from the project's config keys rather than from
 * metric search.
 */
const SEMANTIC_LEGEND_METRIC_TYPES = ['number'];

/**
 * Built-in x-axis keys that metric search does not return; prepended so they
 * stay selectable.
 */
const RESERVED_KEY_OPTIONS: MetricSearchOption[] =
  DEFAULT_X_AXIS_OPTION_VALUES.map(value => ({
    value,
    label: xAxisLabel(value),
  }));

/** Matches the key-info path, which hides system metrics from the key picker. */
const isColorableMetric = (metric: {key: string}) =>
  !isSystemMetric(metric.key);

export type SemanticLegendKeyMetricSearchProps = {
  /** Project config keys, already mapped to options by the parent. */
  configKeyOptions: MetricSearchOption[];
  currentKey: string;
  onSelectKey: (value: string) => void;
};

/**
 * Semantic legend key picker backed by metric search, for the NGW path.
 *
 * Mirrors `SemanticLegendKeyFromKeyInfo`, but sources metric keys from the
 * metric search API rather than the workspace history key info, which NGW
 * leaves empty.
 */
export const SemanticLegendKeyMetricSearch = ({
  configKeyOptions,
  currentKey,
  onSelectKey,
}: SemanticLegendKeyMetricSearchProps) => {
  // Config keys aren't metrics, so metric search never returns them. They ride
  // along with the built-in keys as client-side options.
  const reservedOptions = useMemo(
    () => [...RESERVED_KEY_OPTIONS, ...configKeyOptions],
    [configKeyOptions]
  );

  return (
    <MetricSearchSelect
      ariaLabel="Semantic legend key"
      // Stacks the failure notice over the select, and fills the settings row.
      // min-w-0 lets a long key name shrink instead of widening the drawer.
      className="flex min-w-0 grow flex-col gap-4"
      dataTest="semantic-legend-key-metric-search"
      ddActionName="Semantic legend key"
      filterResult={isColorableMetric}
      labelForValue={xAxisLabel}
      onChange={onSelectKey}
      reservedOptions={reservedOptions}
      types={SEMANTIC_LEGEND_METRIC_TYPES}
      value={currentKey}
    />
  );
};
